import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTenant } from '@/context/tenant-context';

import { useAuth } from '../context/auth-context';
import {
  filterRequestFieldFromMessage,
  useAgentChatHistoryApi,
  useAgentChatHistoryPaginatedApi,
  useAgentChatStreamApi,
} from '../services/agent-chat-service';
import { generateSecureSessionId } from '../services/business-stack-service';
import {
  type AgentChatState,
  type AgentMessage,
  type ChatStreamEvent,
} from '../types/agent-chat';
import { type ChatHistoryItem, type MessageSender } from '../types/agents';
import {
  applyChatStreamEventToMessages,
  cancelPendingAssistantMessage,
  type ChatStreamReducerContext,
  isLikelyPersistedHistoryMessage,
} from '../utils/chat-stream-reducer';
import { extractErrorMessage } from '../utils/error-utils';
import { useConversationState } from './use-conversation-state';
import { usePaginatedChatHistory } from './use-paginated-chat-history';

interface PendingAgentResponse {
  agentMessageId: string;
  agentKey: string;
  userMessage: string;
  userMessageId: string;
  streamMessageId?: string;
  conversationId?: string;
  hasAssistantMessage: boolean;
  hasContent: boolean;
  terminalEvent?: 'final' | 'error';
}

export const useAgentChat = (
  externalMessage?: string,
  externalInjectedMessage?: string,
  shouldLoadChatHistory: boolean = true,
  chatScope: 'dashboard' | 'site' = 'dashboard'
) => {
  const createStableMessageHash = useCallback((value: string): string => {
    let hash = 0;
    for (let index = 0; index < value.length; index += 1) {
      hash = (hash << 5) - hash + value.charCodeAt(index);
      hash |= 0;
    }
    return Math.abs(hash).toString(36);
  }, []);

  const { user } = useAuth();
  const {
    isLoading: isActiveTenantLoading,
    activeAgent,
    setActiveAgent,
    isAgentSwitching,
    waitForAgentReady,
  } = useTenant();
  const [sessionId, setSessionId] = useState(generateSecureSessionId());
  // Tracks which agent is currently waiting on a streamed response, or
  // null if idle. Replaces the previous `isSending: boolean` so that
  // multiple chat bars (Regis / Leanne / AgentAssistantBar) can each
  // know whether IT is the one streaming, instead of all flipping their
  // typing indicator together. `state.isLoading` is derived from this
  // for backward compatibility with the dashboard's chat surfaces.
  const [loadingAgent, setLoadingAgent] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const chatWithAgentStream = useAgentChatStreamApi();
  const fetchPaginatedChatHistory = useAgentChatHistoryPaginatedApi();

  // Wait for agent to be ready before allowing windowed pagination to load
  const isServiceReady =
    !isActiveTenantLoading && !!activeAgent && !isAgentSwitching;

  // Site-scope chat (landing page bars: Regis / Leanne / per-agent
  // assistants) is fire-and-forget POST — there's no persisted
  // conversation to fetch, even when the user is logged in. Hard-gate
  // the history pagination on `chatScope === 'dashboard'` so the
  // landing page never hits the history endpoint.
  const enableHistory =
    chatScope === 'dashboard' && isServiceReady && shouldLoadChatHistory;

  // Initialize paginated chat history with React Query.
  const historyPagination = usePaginatedChatHistory(
    fetchPaginatedChatHistory,
    activeAgent,
    {
      pageSize: 50,
      enabled: enableHistory,
      scope: chatScope,
    }
  );

  const getWelcomeMessage = useCallback((): string => {
    if (user?.firstName) {
      return `Hi ${user.firstName}, What would you like to do today?`;
    }
    return `Hi, What would you like to do today?`;
  }, [user?.firstName]);

  const capitalizeAgentName = useCallback((key: string) => {
    if (!key) return 'Agent';
    return key.charAt(0).toUpperCase() + key.slice(1);
  }, []);

  // Helper to map API history to AgentMessage
  const mapHistoryToAgentMessage = useCallback(
    (item: ChatHistoryItem): AgentMessage => {
      const senderKey = item.sender === activeAgent ? activeAgent : 'user';

      // Ensure proper UTC parsing - if timestamp doesn't end with 'Z', add it
      const timestampStr =
        typeof item.createdAt === 'string'
          ? item.createdAt.endsWith('Z')
            ? item.createdAt
            : `${item.createdAt}Z`
          : item.createdAt;

      return {
        id: `history-${senderKey}-${item.createdAt}-${createStableMessageHash(item.message || '')}`,
        messageId: item.messageId,
        conversationId: item.conversationId,
        sender: senderKey,
        content: item.message,
        timestamp: new Date(timestampStr),
        senderName:
          senderKey === 'user' ? 'You' : capitalizeAgentName(activeAgent || ''),
        createdAt: item.createdAt,
        completedAt: item.completedAt,
        metadata: item.metadata,
      };
    },
    [activeAgent, capitalizeAgentName, createStableMessageHash]
  );

  const historyMessages = useMemo(() => {
    if (!historyPagination?.items?.length) return [];
    const mapped = historyPagination.items.map(mapHistoryToAgentMessage);
    return mapped.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }, [historyPagination?.items, mapHistoryToAgentMessage]);

  const getOptimisticMessagesQueryKey = useCallback(
    (agentKey: string) =>
      ['chatHistory', chatScope, 'optimistic', agentKey] as const,
    [chatScope]
  );

  const activeOptimisticQueryKey = useMemo(() => {
    if (!activeAgent) {
      return ['chatHistory', chatScope, 'optimistic', 'none'] as const;
    }
    return getOptimisticMessagesQueryKey(activeAgent);
  }, [activeAgent, chatScope, getOptimisticMessagesQueryKey]);

  const { data: localMessages = [] } = useQuery({
    queryKey: activeOptimisticQueryKey,
    queryFn: async () => [] as AgentMessage[],
    enabled: Boolean(activeAgent),
    initialData: [],
    staleTime: Infinity,
    cacheTime: 1000 * 60 * 60 * 24,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const updateOptimisticMessages = useCallback(
    (agentKey: string, updater: (prev: AgentMessage[]) => AgentMessage[]) => {
      if (!agentKey) return;
      queryClient.setQueryData<AgentMessage[]>(
        getOptimisticMessagesQueryKey(agentKey),
        previous => updater(previous ?? [])
      );
    },
    [getOptimisticMessagesQueryKey, queryClient]
  );

  const normalizedExternalMessage = useMemo(
    () => externalMessage?.trim() || '',
    [externalMessage]
  );

  const welcomeMessage = useMemo<AgentMessage | null>(() => {
    if (!activeAgent) return null;
    return {
      id: 'welcome-message',
      sender: activeAgent,
      content: getWelcomeMessage(),
      timestamp: new Date(),
      senderName: capitalizeAgentName(activeAgent),
      kind: 'system',
    };
  }, [activeAgent, getWelcomeMessage, capitalizeAgentName]);

  const combinedMessages = useMemo(() => {
    const trimmedLocal =
      historyMessages.length > 0
        ? localMessages.filter(msg => msg.id !== 'welcome-message')
        : localMessages;

    const dedupedLocal = trimmedLocal.filter(
      localMessage =>
        !historyMessages.some(historyMessage =>
          isLikelyPersistedHistoryMessage(localMessage, historyMessage)
        )
    );

    const merged = [...historyMessages, ...dedupedLocal];
    const shouldShowWelcome =
      merged.length === 0 && !historyPagination?.isLoading;

    if (shouldShowWelcome && welcomeMessage) {
      return [welcomeMessage];
    }

    return merged.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }, [
    historyMessages,
    localMessages,
    historyPagination?.isLoading,
    welcomeMessage,
  ]);

  const state = useMemo<AgentChatState>(
    () => ({
      messages: combinedMessages,
      isLoading: loadingAgent !== null,
      sessionId,
    }),
    [combinedMessages, loadingAgent, sessionId]
  );

  const hasMatchingUserMessage = useMemo(() => {
    if (!normalizedExternalMessage) return false;
    return state.messages.some(
      message =>
        message.sender === 'user' &&
        message.content.trim() === normalizedExternalMessage
    );
  }, [state.messages, normalizedExternalMessage]);

  // Function to add external error messages to chat
  const addInjectedMessage = useCallback(
    (injectedMessage: string) => {
      const injectedMsg: AgentMessage = {
        id: `error-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
        sender: activeAgent || '',
        content: injectedMessage,
        timestamp: new Date(),
        senderName: capitalizeAgentName(activeAgent || ''),
      };

      updateOptimisticMessages(activeAgent || '', prev => [
        ...prev,
        injectedMsg,
      ]);
    },
    [activeAgent, capitalizeAgentName, updateOptimisticMessages]
  );

  // Handle external injected messages
  useEffect(() => {
    if (externalInjectedMessage && externalInjectedMessage.trim()) {
      addInjectedMessage(externalInjectedMessage);
    }
  }, [externalInjectedMessage, addInjectedMessage]);

  const scrollToBottomCallbackRef = useRef<(() => void) | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const pendingResponseRef = useRef<PendingAgentResponse | null>(null);

  const getStreamReducerContext = useCallback(
    (pending: PendingAgentResponse): ChatStreamReducerContext => ({
      agentKey: pending.agentKey,
      agentMessageId: pending.agentMessageId,
      streamMessageId: pending.streamMessageId,
      senderName: capitalizeAgentName(pending.agentKey),
      retryPayload: pending.userMessage,
      userMessageId: pending.userMessageId,
      filterText: filterRequestFieldFromMessage,
    }),
    [capitalizeAgentName]
  );

  const sendMessage = useCallback(
    /**
     * Send a message to an agent.
     *  - `messageContent`: user's text.
     *  - `targetAgentKey` (optional): explicit agent override. Pass this
     *    when you've just claimed the agent via `setActiveAgent` and
     *    don't want to risk a stale-closure read of `activeAgent`. The
     *    site-level chat bars (Regis / Leanne / AgentAssistantBar) all
     *    pass it, since their `await setActiveAgent(...)` resolves on a
     *    later React tick and the running closure still holds the
     *    previous active agent. Without this override, messages typed
     *    in one bar were landing in another agent's optimistic queue.
     */
    async (
      messageContent: string,
      targetAgentKey?: string,
      displayMessage?: string
    ) => {
      if (!messageContent.trim() || loadingAgent !== null) return;

      const agentKeyAtSend = targetAgentKey ?? activeAgent ?? '';

      if (isActiveTenantLoading) {
        updateOptimisticMessages(agentKeyAtSend, prev => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            sender: agentKeyAtSend,
            content:
              'Agent is still initializing. Please wait a moment and try again.',
            timestamp: new Date(),
            senderName: capitalizeAgentName(agentKeyAtSend),
          },
        ]);
        return;
      }

      const trimmedMessage = messageContent.trim();
      const visibleMessage = displayMessage?.trim() || trimmedMessage;
      const sendStartedAt = Date.now();
      const userMessageId = `local-user-${sendStartedAt}`;
      const agentMessageId = `local-agent-${sendStartedAt}`;
      const userMessage: AgentMessage = {
        id: userMessageId,
        sender: 'user',
        content: visibleMessage,
        timestamp: new Date(),
        senderName: 'You',
        status: 'completed',
      };

      pendingResponseRef.current = {
        agentMessageId,
        agentKey: agentKeyAtSend,
        userMessage: trimmedMessage,
        userMessageId,
        hasAssistantMessage: false,
        hasContent: false,
      };

      updateOptimisticMessages(agentKeyAtSend, prev => [...prev, userMessage]);
      setLoadingAgent(agentKeyAtSend);

      if (scrollToBottomCallbackRef.current) {
        setTimeout(() => {
          scrollToBottomCallbackRef.current?.();
        }, 100);
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        await chatWithAgentStream({
          userMessage: trimmedMessage,
          sessionId,
          agentKey: agentKeyAtSend,
          signal: controller.signal,
          onEvent: event => {
            const pending = pendingResponseRef.current;
            if (!pending) return;

            if (event.messageId) {
              pending.streamMessageId = event.messageId;
            }
            if (event.conversationId) {
              pending.conversationId = event.conversationId;
            }

            if (event.type !== 'ping' && event.type !== 'done') {
              pending.hasAssistantMessage = true;
            }

            if (
              (event.type === 'delta' || event.type === 'final') &&
              event.text?.trim()
            ) {
              pending.hasContent = true;
            }

            if (event.type === 'final') {
              pending.terminalEvent = 'final';
            }
            if (event.type === 'error') {
              pending.terminalEvent = 'error';
            }

            updateOptimisticMessages(pending.agentKey, prev =>
              applyChatStreamEventToMessages(
                prev,
                event,
                getStreamReducerContext(pending)
              )
            );

            if (event.type === 'done') {
              setLoadingAgent(null);
            }
          },
        });

        const pending = pendingResponseRef.current;
        if (pending && !pending.hasAssistantMessage) {
          const fallbackMessage: AgentMessage = {
            id: pending.agentMessageId,
            sender: pending.agentKey,
            content:
              "I didn't receive a response from the server. Please try again.",
            timestamp: new Date(),
            senderName: capitalizeAgentName(pending.agentKey),
            kind: 'error',
            status: 'failed',
            retry: {
              label: 'Retry',
              payload: pending.userMessage,
            },
          };
          updateOptimisticMessages(pending.agentKey, prev => [
            ...prev,
            fallbackMessage,
          ]);
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        const errorMessage = extractErrorMessage(error);
        const pending = pendingResponseRef.current;
        if (!pending) return;

        const errorEvent: ChatStreamEvent = {
          type: 'error',
          conversationId: pending.conversationId,
          messageId: pending.streamMessageId,
          agentKey: pending.agentKey,
          text: `Something went wrong: ${errorMessage}`,
          status: 'failed',
          metadata: { retryable: true },
          createdAt: new Date().toISOString(),
        };

        pending.hasAssistantMessage = true;
        pending.terminalEvent = 'error';

        updateOptimisticMessages(pending.agentKey, prev =>
          applyChatStreamEventToMessages(
            prev,
            errorEvent,
            getStreamReducerContext(pending)
          )
        );
      } finally {
        setLoadingAgent(null);
        pendingResponseRef.current = null;
        if (abortControllerRef.current?.signal.aborted !== true) {
          abortControllerRef.current = null;
        }
      }
    },
    [
      activeAgent,
      capitalizeAgentName,
      chatWithAgentStream,
      getStreamReducerContext,
      isActiveTenantLoading,
      loadingAgent,
      sessionId,
      updateOptimisticMessages,
    ]
  );

  const stopStreaming = useCallback(() => {
    const pending = pendingResponseRef.current;
    if (pending) {
      updateOptimisticMessages(pending.agentKey, prev =>
        cancelPendingAssistantMessage(prev, getStreamReducerContext(pending))
      );
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    pendingResponseRef.current = null;
    setLoadingAgent(null);
  }, [getStreamReducerContext, updateOptimisticMessages]);

  /**
   * Site-only API: send a message explicitly *as* a given agent. This
   * exists because the per-agent chat bars (Regis / Leanne / per-agent
   * assistants) all share one chat session, but each bar should send
   * to its own agent regardless of who currently holds the
   * `activeAgent` slot.
   *
   * Flow:
   *  1. If the requested agent isn't the active one, claim it. We
   *     await this so the chat session hydrates against the correct
   *     agent config server-side.
   *  2. Call `sendMessage` with the explicit `agentKey` override so
   *     the routing isn't subject to the running closure's stale
   *     `activeAgent` value (which previously caused messages typed
   *     in one bar to land in another agent's optimistic queue).
   */
  const sendMessageAs = useCallback(
    async (
      agentKey: string,
      messageContent: string,
      displayMessage?: string
    ) => {
      if (!agentKey || !messageContent.trim() || loadingAgent !== null) return;
      if (activeAgent !== agentKey) {
        try {
          await setActiveAgent(agentKey);
        } catch {
          return;
        }
      }
      await sendMessage(messageContent, agentKey, displayMessage);
    },
    [activeAgent, loadingAgent, sendMessage, setActiveAgent]
  );

  // Track auto-send to prevent duplicates
  const hasAutoSentExternalMessage = useRef(false);
  const lastExternalMessageRef = useRef<string | null>(null);
  const lastUserMessageRef = useRef<string | null>(null);

  useEffect(() => {
    const lastUserMessage = [...state.messages]
      .reverse()
      .find(message => message.sender === 'user');
    lastUserMessageRef.current = lastUserMessage?.content?.trim() || null;
  }, [state.messages]);

  // Handle external messages (from HeroSection)
  const handleExternalMessage = useCallback(async () => {
    if (shouldLoadChatHistory && historyPagination?.isLoading) {
      return;
    }

    if (normalizedExternalMessage && !hasAutoSentExternalMessage.current) {
      if (
        hasMatchingUserMessage ||
        lastUserMessageRef.current === normalizedExternalMessage
      ) {
        hasAutoSentExternalMessage.current = true;
        return;
      }

      // Double-check that we're targeting the correct agent
      if (activeAgent !== 'regis') {
        return; // Don't send yet, wait for agent to be correct
      }

      // Wait for agent to be fully ready
      const isReady = await waitForAgentReady('regis');
      if (!isReady) {
        return;
      }

      // Final check after waiting
      if (activeAgent === 'regis') {
        hasAutoSentExternalMessage.current = true;
        sendMessage(normalizedExternalMessage);
      } else {
        console.error(
          `Agent still mismatched after waiting: expected regis, got ${activeAgent}`
        );
      }
    }
  }, [
    activeAgent,
    hasMatchingUserMessage,
    historyPagination?.isLoading,
    normalizedExternalMessage,
    sendMessage,
    shouldLoadChatHistory,
    waitForAgentReady,
  ]);

  useEffect(() => {
    const nextExternal = normalizedExternalMessage;
    if (lastExternalMessageRef.current !== nextExternal) {
      hasAutoSentExternalMessage.current = false;
      lastExternalMessageRef.current = nextExternal || null;
    }
    handleExternalMessage();
  }, [handleExternalMessage, normalizedExternalMessage]);

  // Group messages by date (YYYY-MM-DD)
  const groupMessagesByDate = () => {
    const grouped: Record<string, AgentMessage[]> = {};
    const sorted = [...state.messages].sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    );
    sorted.forEach(msg => {
      const dateKey = msg.timestamp.toISOString().split('T')[0]; // YYYY-MM-DD format
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(msg);
    });
    return grouped;
  };

  const reloadChatHistory = useCallback(async () => {
    historyPagination.reset({ invalidate: true });
  }, [historyPagination]);

  // Function to set the scroll callback
  const setScrollToBottomCallback = useCallback((callback: () => void) => {
    scrollToBottomCallbackRef.current = callback;
  }, []);

  useEffect(() => {
    if (!activeAgent) return;
    stopStreaming();
    setSessionId(generateSecureSessionId());
  }, [activeAgent, stopStreaming]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    state,
    sendMessage,
    sendMessageAs,
    loadingAgent,
    stopStreaming,
    groupMessagesByDate,
    reloadChatHistory,
    addInjectedMessage,
    historyPagination,
    setScrollToBottomCallback,
  };
};

export const useAgentChatHistory = () => {
  const fetchChatHistory = useAgentChatHistoryApi();
  const { addMessage } = useConversationState();
  const { activeAgent } = useTenant();

  // Converts API history item to ChatMessage format
  const mapHistoryToChatMessage = (item: ChatHistoryItem) => {
    const sender: MessageSender =
      item.sender === activeAgent ? activeAgent : 'user';

    // Ensure proper UTC parsing - if timestamp doesn't end with 'Z', add it
    const timestampStr =
      typeof item.createdAt === 'string'
        ? item.createdAt.endsWith('Z')
          ? item.createdAt
          : `${item.createdAt}Z`
        : item.createdAt;

    return {
      id: `${sender}-${item.createdAt}`,
      sender,
      content: filterRequestFieldFromMessage(item.message),
      timestamp: new Date(timestampStr),
      senderName: sender === 'user' ? 'You' : activeAgent,
    };
  };

  // Loads and adds chat history to state, sorted by date ascending
  const loadChatHistory = useCallback(async () => {
    const history = await fetchChatHistory();
    // Sort by createdAt ascending
    const sorted = history.sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    // Add each message to state
    sorted.forEach(item => {
      if (!activeAgent) return;
      const msg = mapHistoryToChatMessage(item);
      addMessage(msg.sender, msg.content, msg?.senderName || '');
    });
  }, [fetchChatHistory, addMessage]);

  return { loadChatHistory };
};
